import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';

import { translate } from '@jsverse/transloco';

import { SemantriaPreferences } from 'app/providers/bridge-api/preferences.service';
import { LanguageTemplateService } from 'app/providers/semantria-api/language-template.service';
import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import { Collection } from 'app/providers/storage-api/collection.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ConfigurationUtils } from 'app/utils/configuration.utils';
import { ConfigurationRoute } from '../../../../providers/semantria-api/configuration-route.service';
import { ConfigurationRouteService } from '../../../../providers/semantria-api/configuration-route.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from '../../../../providers/semantria-api/configuration.service';

import { GroupService } from '../../../../providers/semantria-api/group.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-analysis-config-selector',
  templateUrl: './analysis-config-selector.component.html',
  styleUrls: ['./analysis-config-selector.component.scss'],
  standalone: false,
})
export class AnalysisConfigSelectorComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() disabled: boolean;
  @Input() suggestLastUsed = true;
  @Input() selectedConfigId: string;
  @Input() collection: Collection;
  @Input() preloadedGroupConfigs: SemantriaConfiguration[];

  @Output() selectedConfig = new EventEmitter<SemantriaConfiguration | ConfigurationRoute>();
  @Output() selectedRouteLang = new EventEmitter<string>();
  @Output() isLoading = new EventEmitter<boolean>();

  loading: boolean;
  public configCount = 0;
  public lastConfigId: string;
  public lastConfig: SemantriaConfiguration & ConfigurationRoute;
  public defaultConfigName = 'English';

  public configurations = {};
  public filteredConfigurations = {};
  public languages: string[] = [];

  public configSelectControl = new UntypedFormControl();
  public selectedConfiguration: SemantriaConfiguration | ConfigurationRoute = null;
  public routeLanguageControl = new UntypedFormControl();
  public selectedRouteLanguage = null;

  private searchTerm: string;

  constructor(
    private configService: ConfigurationService,
    private groupService: GroupService,
    private accountService: SemantriaAccountService,
    private languageTemplateService: LanguageTemplateService,
    private configRouteService: ConfigurationRouteService,
    private preferencesService: SemantriaPreferences
  ) {}

  async ngOnInit() {
    if (this.disabled) {
      this.configSelectControl.disable();
      this.routeLanguageControl.disable();
    }
    this.loading = true;
    this.isLoading.emit(this.loading);

    await this.loadConfigurations();

    if (!this.disabled && this.configCount) {
      this.configSelectControl.enable();
      this.routeLanguageControl.enable();
    }

    if (this.lastConfig) {
      this.selectedConfiguration = this.lastConfig;
      this.configSelectControl.setValue(this.selectedConfiguration);
      this.selectedRouteLanguage = 'auto';
      this.routeLanguageControl.setValue('auto');
      if (
        (this.lastConfig.id && this.lastConfig.id !== this.selectedConfigId) ||
        (this.lastConfig.route_id && this.lastConfig.route_id !== this.selectedConfigId)
      ) {
        this.selectedConfig.emit(this.selectedConfiguration);
        this.selectedRouteLang.emit(this.selectedRouteLanguage);
      }
    }

    this.loading = false;
    this.isLoading.emit(this.loading);
  }

  public filterConfigurations($event: any): void {
    $event.preventDefault();
    this.filteredConfigurations = {};
    const keyword = $event.target.value.toLowerCase();
    if (keyword !== '') {
      if (this.searchTerm !== keyword && $event.key !== 'Enter') {
        this.selectedConfiguration = undefined;
      }
      this.searchTerm = keyword;
      for (const lang of Object.keys(this.configurations)) {
        const confsForLang = this.configurations[lang].filter((conf) =>
          conf.name.toLowerCase().includes(keyword)
        );
        if (confsForLang.length) {
          this.filteredConfigurations[lang] = confsForLang;
        }
      }
    } else {
      this.filteredConfigurations = this.configurations;
    }
    this.languages = Object.keys(this.filteredConfigurations);
  }

  async loadConfigurations() {
    this.configCount = 0;

    const detectLastConfig = (config) => {
      if (
        (config.id && config.id === this.lastConfigId) ||
        (config.route_id && config.route_id === this.lastConfigId)
      ) {
        this.lastConfig = config;
      }

      //TODO: probably need better default detection.  DASH-1798 specified "English"
      //We don't have a default universal English yet.
      if (this.suggestLastUsed && config.name === this.defaultConfigName && !this.lastConfig) {
        this.lastConfig = config;
      }
    };

    let groupConfigs;
    if (this.preloadedGroupConfigs) {
      groupConfigs = this.preloadedGroupConfigs;
    } else {
      groupConfigs = await this.configService.getConfigurations(
        false,
        this.groupService.getSelectedGroupId()
      );
    }

    this.lastConfigId = this.selectedConfigId || '';

    if (this.collection && this.collection.configId) {
      this.lastConfigId = this.collection.configId;
    } else {
      if (this.suggestLastUsed && !this.lastConfigId) {
        const preferences = await this.preferencesService.getPreferences(true);
        //If our last config is a normal config search for it in the available configs
        if (preferences.lastConfig?.split(':').length === 1) {
          if (
            !groupConfigs.some((config) => {
              return config.id === preferences.lastConfig;
            })
          ) {
            this.lastConfigId = undefined;
          } else {
            this.lastConfigId = preferences.lastConfig;
          }
        } else {
          this.lastConfigId = preferences.lastConfig?.split(':')[1];
        }
      }
    }

    if (this.lastConfigId && this.lastConfigId.split(':').length > 1) {
      this.lastConfigId = this.lastConfigId.split(':')[1];
    }

    for (const config of groupConfigs) {
      if (!this.configurations[config.language_name]) {
        this.configurations[config.language_name] = [];
      }

      this.configurations[config.language_name].push(config);
      this.configCount++;

      detectLastConfig(config);
    }

    const accountDetails = await this.accountService.sub.latest();
    const languageTemplates = await this.languageTemplateService.getLanguageTemplates();
    const configRoutes = await this.configRouteService.getConfigurationRoutes(
      this.groupService.getSelectedGroupId()
    );

    if (accountDetails.industry_packs) {
      for (const pack of accountDetails.industry_packs) {
        this.configCount++;
        this.configurations[pack.language_name] = this.configurations[pack.language_name] || [];
        this.configurations[pack.language_name].push({ ...pack, isIndustryPack: true });
        pack.isIndustryPack = true;
        detectLastConfig(pack);
      }
    }

    for (const lang of languageTemplates) {
      if (!lang.id.endsWith('prm')) {
        this.configCount++;
        this.configurations[lang.language_name] = this.configurations[lang.language_name] || [];
        this.configurations[lang.language_name].push({ ...lang, isLanguageTemplate: true });
        lang.isLanguageTemplate = true;
        detectLastConfig(lang);
      }
    }

    for (const route of configRoutes) {
      this.configCount++;
      this.configurations['Configuration Routes'] =
        this.configurations['Configuration Routes'] || [];
      this.configurations['Configuration Routes'].push({ ...route, isConfigurationRoute: true });
      route.isConfigurationRoute = true;
      detectLastConfig(route);
    }

    this.languages = Object.keys(this.configurations);
    this.filteredConfigurations = this.configurations;
    return;
  }

  public onConfigSelected($event: MatAutocompleteSelectedEvent): void {
    this.selectedConfiguration = $event.option.value;
    if ('isConfigurationRoute' in this.selectedConfiguration) {
      this.selectedRouteLanguage = 'auto';
      this.routeLanguageControl.setValue(this.selectedRouteLanguage);
    }
    this.selectedConfig.emit(this.selectedConfiguration);
  }

  public onRouteLangChanged($event: MatSelectChange): void {
    this.selectedRouteLanguage = $event.value;
    this.selectedRouteLang.emit(this.selectedRouteLanguage);
  }

  public get configRouteSelected() {
    if (!this.selectedConfiguration) {
      return false;
    }

    return 'isConfigurationRoute' in this.selectedConfiguration
      ? this.selectedConfiguration.isConfigurationRoute
      : false;
  }

  public configDisabled(config) {
    if (config.isConfigurationRoute) {
      return config.response_type !== 'POLLING' || Object.keys(config.configuration_map).length < 1;
    }
    return config.callback_url || config.is_autoresponse_enabled;
  }

  public configTooltip(config) {
    if (config.isConfigurationRoute) {
      if (config.response_type !== 'POLLING') {
        return translate('analysis-config-selector.config-not-polling');
      }
      if (Object.keys(config.configuration_map).length < 1) {
        return translate('analysis-config-selector.no-configs-in-route');
      }
    } else if (config.callback_url || config.is_autoresponse_enabled) {
      return translate('analysis-config-selector.config-not-polling');
    }
  }

  public displayConfiguration(config?: SemantriaConfiguration): string | undefined {
    return config ? config.name : undefined;
  }

  public get selectedRouteLanguages() {
    if ('isConfigurationRoute' in this.selectedConfiguration) {
      return ['auto'].concat(Object.keys(this.selectedConfiguration.configuration_map));
    }
    return ['auto'];
  }

  getTemplateType(config) {
    if (config.isLanguageTemplate) {
      return 'language';
    } else if (config.isIndustryPack) {
      return 'pack';
    } else if (config.isConfigurationRoute) {
      return 'route';
    }
    return 'configuration';
  }

  hasEieTag(config) {
    return ConfigurationUtils.hasEieTag(config);
  }

  getEieTagTooltipText(config) {
    return ConfigurationUtils.getEieTagTooltipText(config);
  }
}
